<template>
  <div>
    <v-dialog
      :value="dialog"
      :max-width="600"
      persistent
      :retain-focus="false"
      @keydown.esc="escape(closeDialog)"
      @click:outside="escape(closeDialog)"
      @keydown.enter="submit"
    >
      <v-card outlined class="p-members-edit-dialog">
        <v-card-title>
          {{ dialogTitle }}
          <p-btn-icon
            type="close"
            btn-class="mr-n3 ml-auto"
            tabindex="4"
            @click="escape(closeDialog)"
          ></p-btn-icon>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <v-form ref="form" @submit.stop.prevent>
            <v-row class="pt-5" v-if="!this.member || !this.member.contactId">
              <v-col sm="12">
                <v-autocomplete
                  ref="autocomplete"
                  v-model="editedMember.contactId"
                  v-if="contactSuggestions && contactSuggestions.length"
                  :items="contactSuggestions"
                  :rules="rules.contactId"
                  :return-object="false"
                  :label="$t('member.edit.contact')"
                  item-value="id"
                  item-text="displayNameWithCompany"
                  tabindex="1"
                  autofocus
                  hide-selected
                >
                  <template v-slot:append-item>
                    <v-divider></v-divider>
                    <div class="p-list-add-item-link" @click="editContactDialog = true">
                      {{ $t('contact.addNewContact') }}
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row :class="{'pt-5': this.member && this.member.contactId}">
              <v-col sm="12">
                <v-text-field
                  type="text"
                  v-if="editedMember.contactId === 'anonymous'"
                  v-model.trim="editedMember.name"
                  :label="$t('member.edit.name')"
                  :placeholder="$t('member.edit.name')"
                  tabindex="1"
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row :class="{'pt-5': this.member && this.member.contactId}">
              <v-col sm="6">
                <v-text-field
                  type="text"
                  v-model.trim="editedMember.function"
                  :label="$t('member.edit.function')"
                  tabindex="1"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.travelCost"
                  :label="$t('member.edit.travelCost')"
                  tabindex="1"
                  clearable
                ></p-number-input>
              </v-col>
            </v-row>

            <div class="subtitle-1">{{ $t('member.edit.diet') }}</div>
            <v-row>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.dietDays"
                  :label="$t('member.edit.dietDays')"
                  tabindex="1"
                  clearable
                  integer
                ></p-number-input>
              </v-col>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.dietPerDay"
                  :label="$t('member.edit.dietPerDay')"
                  tabindex="1"
                  clearable
                ></p-number-input>
              </v-col>
            </v-row>

            <div class="subtitle-1">{{ $t('member.edit.hotel') }}</div>
            <v-row>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.hotelDays"
                  :label="$t('member.edit.hotelDays')"
                  tabindex="1"
                  clearable
                  integer
                ></p-number-input>
              </v-col>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.hotelPerDay"
                  :label="$t('member.edit.hotelPerDay')"
                  tabindex="1"
                  clearable
                ></p-number-input>
              </v-col>
            </v-row>

            <v-row>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.otherCost"
                  :label="$t('member.edit.otherCost')"
                  tabindex="1"
                  clearable
                ></p-number-input>
              </v-col>
              <v-col sm="6">
                <p-number-input
                  v-model="editedMember.wage"
                  :label="$t('member.edit.wage')"
                  tabindex="1"
                  clearable
                ></p-number-input>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>

        <v-card-actions class="justify-center">
          <v-btn
            tabindex="3"
            clearable
            text
            @click="closeDialog">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn :disabled="!isModelChanged()"
                 color="primary"
                 tabindex="2"
                 clearable
                 text
                 @click="submit">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <contact-edit-dialog
      :dialog.sync="editContactDialog"
      :contact="null"
      @add="contactAdded"
    ></contact-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PNumberInput from '@/components/PNumberInput'
import PBtnIcon from '@/components/PBtnIcon'

import ContactEditDialog from '@/views/Contacts/ContactEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    PNumberInput,
    PBtnIcon,
    ContactEditDialog
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    member: Object
  },


  data () {
    return {
      editContactDialog: false,
      editedMember: {}
    }
  },


  computed: {
    ...mapGetters('contact', ['shortContacts']),
    ...mapGetters('member', ['membersByProject']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('settings', ['currentSettings']),

    dialogPurpose () {
      if (this.member && !this.member.contact) {
        return 'copyMember'
      }
      if (this.member && !this.member.contact.id) {
        return 'editAnon'
      }
      if (this.member && this.member.id) {
        return 'editMember'
      }
      return 'addMember'
    },

    dialogTitle () {
      if (this.dialogPurpose === 'copyMember') {
        return this.$t('member.copyMember')
      }
      if (this.dialogPurpose === 'editAnon') {
        return this.$t('member.editMemberAnonymous', { name: this.editedMember.contact?.displayNameWithCompany })
      }
      if (this.dialogPurpose === 'editMember') {
        return this.$t('member.editMember', { name: this.editedMember.contact?.displayNameWithCompany })
      }
      return this.$t('member.addMember')
    },

    defaultMember () {
      return {
        contactId: '',
        projectId: '',
        function: '',
        travelCost: 0,
        dietDays: 0,
        dietPerDay: this.currentSettings.diet ?? 0,
        hotelDays: 0,
        hotelPerDay: this.currentSettings.hotel ?? 0,
        otherCost: 0,
        wage: 0,
        totalCost: 0
      }
    },

    anonymousContactSuggestion () {
      return {
        id: 'anonymous',
        displayNameWithCompany: this.$t('member.anonymousMember')
      }
    },

    contactSuggestions () {
      const members = this.membersByProject(this.currentProjectId, false)
      const contactSuggestions = this.shortContacts.filter(contact => !members.find(member => member.contactId === contact.id))
      contactSuggestions.unshift(this.anonymousContactSuggestion)
      return contactSuggestions
    },

    rulesToApply () {
      const rule = this.rule
      return rule
    }
  },


  methods: {
    ...mapActions('member', ['createMember', 'updateMember']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.member) {
        this.removeFromChanges(this.member)
      }
      this.editedMember = this.getObjectCopy(this.member || this.defaultMember)
      this.resetValidation()
      setTimeout(() => {
        if (this.$refs.autocomplete) {
          this.$refs.autocomplete.isMenuActive = true
        }
      }, 600)
      this.initWatchedModel(this.editedMember)
    },

    contactAdded (contact) {
      this.editedMember.contactId = contact.id
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const member = this.getObjectCopy(this.editedMember)
        delete member.contact
        if (member.contactId === 'anonymous') {
          delete member.contactId
        } else {
          delete member.name
        }

        if (this.dialogPurpose === 'addMember' || this.dialogPurpose === 'copyMember') {
          member.projectId = this.currentProjectId
          await this.createMember(member)
          this.showSnackbarSuccess(this.$t('member.memberAdded'))
        } else if (this.dialogPurpose === 'editMember' || this.dialogPurpose === 'editAnon') {
          await this.updateMember(member)
          this.showSnackbarSuccess(this.$t('member.memberUpdated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
